import type { BookingSkierFormData } from '@/store/book-ski-pass.store';
import type { CheckoutPassSelectionDto } from '@/services/orders.service';
import { isValidBirthDate } from '@/utils/booking';
import {
  isBirthDateMatchingNamedCategory,
  type DomainRestriction,
} from '@/utils/domain-restrictions';
import { expandPassSelections } from '@/utils/skier-slots';

function normalizeCategory(s: string): string {
  return s.trim().toLowerCase();
}

/**
 * Clears birth dates when moving from step 2 to step 3 if the pass category at an index
 * changed vs the last saved order, or if a filled DOB does not match the newly selected pass line.
 */
export function sanitizeSkierFormsForStepTwoToThree(
  skierForms: BookingSkierFormData[],
  previousOrderPassSelections: CheckoutPassSelectionDto[],
  nextPassSelections: CheckoutPassSelectionDto[],
  domainRestrictionsMap: Map<number, DomainRestriction[]>,
  startDate: string
): BookingSkierFormData[] {
  const prevSlots = expandPassSelections(previousOrderPassSelections);
  const nextSlots = expandPassSelections(nextPassSelections);
  const n = nextSlots.length;
  const result: BookingSkierFormData[] = [];

  for (let i = 0; i < n; i++) {
    const base = skierForms[i] ?? {
      skipassId: undefined,
      firstName: '',
      lastName: '',
      birthDate: '',
    };
    const slot = nextSlots[i];
    if (!slot) {
      result.push(base);
      continue;
    }

    const bd = base.birthDate?.trim() ?? '';
    let clearBirth = false;

    const prevCat = prevSlots[i]?.ageCategory;
    if (prevCat != null && normalizeCategory(prevCat) !== normalizeCategory(slot.ageCategory)) {
      clearBirth = true;
    }

    if (!clearBirth && bd && isValidBirthDate(bd)) {
      const restrictions = domainRestrictionsMap.get(slot.domain.id) ?? [];
      if (!isBirthDateMatchingNamedCategory(bd, restrictions, slot.ageCategory, startDate)) {
        clearBirth = true;
      }
    }

    result.push(clearBirth ? { ...base, birthDate: '' } : { ...base });
  }

  return result;
}
